import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Heart, 
  Music, 
  Palette, 
  Store, 
  ArrowRight, 
  ExternalLink,
  MapPin,
  Star,
  ChevronDown,
  Users,
  DollarSign,
  BookOpen,
  Shield,
  HelpCircle,
  Calendar as CalendarIcon,
  Ticket,
  Guitar
} from "lucide-react";
import { NorthStarStrip, StarPathMark } from "../components/NorthStarSection";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import HappeningMap from "../components/HappeningMap";
import PageSectionEditor from "../components/admin/PageSectionEditor";
import { InlineHoardPlayer } from "../player/InlineHoardPlayer";
import WelcomeTour from "../components/WelcomeTour";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import ResourceLibraryCallout from "../components/ResourceLibraryCallout";
import { SpikingSignsBadge } from "../components/SpikingSignsBadge";
import UpcomingFestivalsRow from "../components/UpcomingFestivalsRow";
import { orgFeaturePage } from "../utils/orgLinks";
import { Hoard } from "../components/Hoard";
import { VettedLabel } from "../components/VettedLabel";
import { OrgsSection } from "../components/OrgsSection";
import { RadioPill } from "../radio/RadioDock";

const LandingPage = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [stats, setStats] = useState({});
  const [causes, setCauses] = useState([]);
  const [homeCause, setHomeCause] = useState("all"); // eslint-disable-line no-unused-vars
  const [selectedCause, setSelectedCause] = useState(null);
  const [upcomingEventCount, setUpcomingEventCount] = useState(0);
  const [nextEvents, setNextEvents] = useState([]);
  const [businessCount, setBusinessCount] = useState(0);
  const [featuredArtists, setFeaturedArtists] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgsRes, statsRes, causesRes, eventsRes, businessesRes, artistsRes] = await Promise.all([
          axios.get(`${API}/organizations`),
          axios.get(`${API}/stats`),
          axios.get(`${API}/causes`),
          axios.get(`${API}/events`).catch(() => ({ data: [] })),
          axios.get(`${API}/directory/businesses`).catch(() => ({ data: [] })),
          axios.get(`${API}/featured-artists`).catch(() => ({ data: [] })),
        ]);
        setOrganizations(orgsRes.data);
        setAllOrganizations(orgsRes.data);
        setStats(statsRes.data);
        setCauses(causesRes.data);
        setUpcomingEventCount(eventsRes.data.length);
        setNextEvents(eventsRes.data.slice(0, 3));
        setBusinessCount(businessesRes.data.length);
        // Featured spotlight pair: Walker Sherman + Dain Norman
        const bySlug = Object.fromEntries((artistsRes.data || []).map(a => [a.slug, a]));
        setFeaturedArtists([bySlug["walker-sherman"], bySlug["dain-norman"]].filter(a => a && a.photo_url));
      } catch (error) {
        console.error("LandingPage data fetch failed:", error);
      }
    };
    fetchData();
  }, []);

  // Filter organizations by selected cause
  const handleCauseFilter = (causeId) => {
    if (causeId === selectedCause) {
      // Deselect - show all
      setSelectedCause(null);
      setOrganizations(allOrganizations);
    } else {
      setSelectedCause(causeId);
      setOrganizations(allOrganizations.filter(org => 
        (org.causes || []).includes(causeId)
      ));
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Standardized site Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-8 md:pt-32 md:pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2E5C55]/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              {/* Tagline above the banner */}
              <div className="mb-4 text-center">
                <Link 
                  to="/contribute" 
                  className="text-xl text-[#2E5C55] hover:text-[#E76F51] font-bold transition-colors"
                  data-testid="landing-for-bands-cta"
                >
                  An Emergent ALL-ONE Community Multi-Solution that (truly) rocks
                </Link>
              </div>

              {/* Hearts Call All Banner */}
              <div className="mb-4 rounded-xl overflow-hidden shadow-lg relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1dd5d7b5-0f99-4c93-98c4-f04065290914/artifacts/e6zotw6x_the%20wall%20marty%20hearts%20call%20all.png"
                  alt="Hearts Call ALL to THE WALL - Give & Let Live (featuring Martin of MTR PROJECT)"
                  className="w-full h-auto"
                />
                <Link
                  to="/our-origins"
                  className="absolute -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-full bg-white pl-3.5 pr-5 py-1 text-xs sm:text-sm font-black tracking-[0.18em] text-[#1E3A8A] shadow-lg opacity-80 [text-shadow:0_0_0.6px_currentColor] hover:opacity-100 transition-opacity no-underline whitespace-nowrap"
                  style={{ left: "57%", top: "79%" }}
                  data-testid="hero-banner-values-btn"
                >
                  <StarPathMark className="h-5 w-5 ring-[#1E3A8A]/30" /> Our ORIGINS
                </Link>
                {/* ON AIR button — opens the music player (Dolly) */}
                <RadioPill className="absolute aspect-square -translate-x-1/2 -translate-y-1/2" size={null} style={{ left: "17%", top: "73%", width: "clamp(60px, 18.5%, 180px)", "--onair": "clamp(9px, 1.9vw, 13px)" }} testId="home-radio-pill" />
              </div>

              {/* North Star spiking guide — directly under the banner, above the song */}
              <NorthStarStrip />

              {/* Tech safety — VPN nudge (Mullvad) */}
              <a href="https://mullvad.net/en" target="_blank" rel="noopener noreferrer" className="mb-6 flex items-center justify-between gap-4 rounded-2xl border-2 border-[#1A1A1A]/10 bg-[#1b2a44] px-5 py-3 text-white hover:border-[#1D4ED8] transition-colors" data-testid="home-vpn-strip">
                <span className="text-sm md:text-base font-bold leading-snug"><span className="text-[#F2CC8F]">The Sith are real:</span> <strong className="font-extrabold uppercase tracking-wide">Use this site with a trusted VPN</strong> <span className="underline decoration-2 underline-offset-2 text-[#9EC5FF]">→ Mullvad</span></span>
                <img src="/mullvad.png" alt="Protect your safety — VPN" className="h-14 w-14 shrink-0 rounded-xl object-cover ring-2 ring-white/20" />
              </a>

              {/* Featured Music Player - MTR PROJECT */}
              <div className="mb-8">
                <p className="text-sm text-[#5C5C5C] mb-2 text-center italic">Listen to music for THE WALL — it keeps playing while you browse</p>
                <InlineHoardPlayer />
                {/* Featured badge — admin-editable (page_layouts key home:featured-badge) */}
                <div className="mt-3" data-testid="hero-featured-badge">
                  <PageSectionEditor layoutKey="home:featured-badge" emptyMessage="Add a featured badge here." />
                </div>
                {/* Expiring flash ad — admin-editable (page_layouts key home:flash-ad). Badges with show_until auto-hide. */}
                <div className="mt-3" data-testid="hero-flash-ad">
                  <PageSectionEditor layoutKey="home:flash-ad" emptyMessage="Add an expiring ad here." />
                </div>
              </div>

              {/* Admin-curated Upcoming Festivals — populates from Featuring
                  → home.upcoming_festivals slot. When empty, renders nothing.
                  Once Porchfest is over, admin adds the next festival here
                  and this row swaps in seamlessly. */}
              <UpcomingFestivalsRow />

              {/* Happening Around the Sound — This Week events map / Night Life after 7pm (directly under the festivals row) */}
              <div id="happening-map" className="mb-6 rounded-xl overflow-hidden shadow-lg bg-white p-4 border border-[#E2E0DC] scroll-mt-24">
                <HappeningMap height="460px" />
              </div>

              {/* How THE WALL Works - Bullet Points */}
              <div className="text-lg text-[#5C5C5C] mb-8 space-y-3">
                <p className="flex items-start gap-2">
                  <span className="text-[#2E5C55] font-semibold">•</span>
                  <span><strong className="text-[#2E5C55]">Find</strong> your fun on the <Link to="/events/music" className="text-[#2E5C55] hover:text-[#E76F51] underline">Live Local music</Link> calendar, get into <Link to="/events" className="text-[#2E5C55] hover:text-[#E76F51] underline">community events here</Link> and discover quality <Link to="/events" className="text-[#2E5C55] hover:text-[#E76F51] underline" data-testid="landing-bullet-local-biz-care">local businesses that care</Link></span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-[#2E5C55] font-semibold">•</span>
                  <span>Get local deals, music and more when you give to a community orgs <Link to="/map" className="text-[#E76F51] hover:text-[#2E5C55] underline font-semibold" data-testid="landing-bullet-orgs-here">HERE</Link> — your gift goes directly to the organization through its own donation page; THE WALL never touches the money. Whether it's tax-deductible depends on your situation and any perks you receive — check with a tax advisor. Find more orgs in the <Link to="/resources" className="text-[#2E5C55] hover:text-[#E76F51] underline">Resource Library</Link></span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-[#2E5C55] font-semibold">•</span>
                  <span><strong className="text-[#2E5C55]">Discover</strong> — Get relevant real-world info by field and find community resources in our <Link to="/resources" className="text-[#2E5C55] hover:text-[#E76F51] underline">Resource Library</Link></span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-[#2E5C55] font-semibold">•</span>
                  <span><strong className="text-[#2E5C55]">ADD Your Touch</strong> to THE WALL: BANDS go <Link to="/for-bands" className="text-[#E76F51] hover:text-[#2E5C55] underline font-semibold" data-testid="landing-bullet-bands-here">HERE</Link>; BUSINESSES <Link to="/for-businesses" className="text-[#E76F51] hover:text-[#2E5C55] underline font-semibold" data-testid="landing-bullet-businesses-here">HERE</Link> &amp; ARTISTS <Link to="/contribute" className="text-[#E76F51] hover:text-[#2E5C55] underline font-semibold" data-testid="landing-bullet-artists-here">HERE</Link> <span className="text-[#2E5C55] font-semibold" data-testid="landing-bullet-give-get-more">GIVE &amp; GET MORE</span></span>
                </p>
                <p className="text-xl font-semibold text-[#2E5C55] mt-4">
                  Everybody WINS!
                </p>
              </div>
              
              {/* Resource Library + Safety callout — compact-by-default.
                  The big "We gather to celebrate" + emergency-contacts panel
                  is collapsed into a drop-down ("Life in the Scene") so the
                  Resource Library framing stays the lead, and the safety detail
                  is still one tap away. */}
              <div className="mb-8" data-testid="home-resource-library">
                <div className="relative rounded-2xl overflow-hidden border-2 border-[#2E5C55]/20 bg-gradient-to-br from-[#F4F8F6] to-[#E8F0EC] p-6 sm:p-8">
                  {/* Header is fully clickable — icon + title + tagline all
                      route to /resources so the whole top of the card behaves
                      like one big "open the library" button. */}
                  <Link
                    to="/resources"
                    className="flex items-start gap-3 mb-4 group no-underline sm:pr-56"
                    data-testid="home-resource-header-link"
                  >
                    <div className="flex-shrink-0 bg-gradient-to-r from-pink-500 to-purple-600 p-2.5 rounded-xl shadow-md group-hover:shadow-lg group-hover:scale-[1.04] transition-all">
                      <BookOpen className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-0.5 group-hover:text-[#E76F51] transition-colors">
                        Resource Library
                      </h3>
                      <p className="text-sm text-[#5C5C5C] group-hover:text-[#2E5C55] transition-colors">
                        Knowledge is protection
                      </p>
                    </div>
                  </Link>
                  <div className="-mt-2 mb-4 sm:absolute sm:top-5 sm:right-5 sm:mt-0 sm:mb-0" data-testid="home-spiking-badge-wrap">
                    <SpikingSignsBadge testId="home-spiking-badge" />
                  </div>

                  {/* Body text — keyword phrases are linked + accent-colored
                      to invite a click into the Resource Library. */}
                  <p className="text-[#3D3D3D] leading-relaxed text-sm sm:text-base mb-5">
                    Find guides to resources across fields, from{" "}
                    <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-food">food</Link>,{" "}
                    <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-housing">housing</Link>,{" "}
                    <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-relationships">relationships</Link>,{" "}
                    <Link to="/resources?cause=arts" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-arts-publications">arts publications</Link>,{" "}
                    <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-safety">safety</Link>
                    , <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-much-more">&amp; much more...</Link>{" "}
                    learn how to{" "}
                    <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-spot-abuse">spot abuse</Link>
                    , <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-food-banks">find food banks</Link>
                    {" "}and many other resources.
                  </p>

                  {/* Collapsible: IN THE SCENE — drink-spiking + "harmful crowd" door + resource callout. */}
                  <Accordion type="single" collapsible className="mb-4">
                    <AccordionItem value="safety" className="border-t border-[#2E5C55]/15 pt-1">
                      <AccordionTrigger
                        className="hover:no-underline py-3 text-left"
                        data-testid="home-safety-accordion-trigger"
                      >
                        <div className="flex items-center gap-3 pr-2">
                          <Shield className="h-5 w-5 text-[#2E5C55] flex-shrink-0" />
                          <span className="font-['Playfair_Display'] text-xl sm:text-2xl font-bold tracking-wide text-[#2E5C55]" data-testid="home-in-the-scene-title">IN THE SCENE</span>
                          <span className="hidden sm:inline text-sm text-[#5C5C5C] italic">— look out for each other</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-1" data-testid="home-safety-accordion-content">
                        <p className="text-sm text-[#1A1A1A] leading-relaxed mb-3">
                          <span className="italic text-[#2E5C55]" data-testid="home-north-star-line">
                            The worst hangovers aren&apos;t always hangovers. Every group needs a North Star.
                          </span>{" "}
                          <Link to="/safety" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-drink-spiking">
                            Issues like drink-spiking are urgent
                          </Link>
                          {" "}to know about for everyone now: Get{" "}
                          <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-real-resources">
                            real resources
                          </Link>
                          {" "}and learn how to{" "}
                          <Link to="/resources" className="font-bold text-[#E76F51] hover:text-[#C0392B] underline decoration-[#E76F51]/50 hover:decoration-[#C0392B] transition-colors" data-testid="rl-link-recognize-abuse">
                            recognize abuse
                          </Link>
                          {" "}before it happens — and what to do when it happens to you or someone close.
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Link to="/abuse#the-group" className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white px-4 py-2.5 text-sm font-extrabold hover:bg-black transition-colors no-underline" data-testid="home-harmful-crowd-btn">
                            <StarPathMark className="h-5 w-5" /> In a harmful crowd? Know this: →
                          </Link>
                          <SpikingSignsBadge testId="home-spiking-badge-inline" />
                        </div>
                        <ResourceLibraryCallout className="mt-2" />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Link
                    to="/resources"
                    className="inline-flex items-center gap-2 bg-[#2E5C55] hover:bg-[#234a44] text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors no-underline"
                    data-testid="home-open-resource-library"
                  >
                    Open the Resource Library
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              
              {/* ON THE WALL — one door to the HOARD (music · Gems · art) */}
              <div className="flex justify-center mb-8">
                <Link to="/passport" className="no-underline" data-testid="hero-starport-btn">
                  <Button className="rounded-full h-auto whitespace-normal text-center px-6 py-4 text-sm sm:text-base font-bold bg-[#120A24] text-white border-2 border-[#54E3FF] hover:bg-[#1a1033] shadow-lg max-w-[90vw]">
                    <Music className="mr-2 h-4 w-4 text-[#54E3FF] shrink-0" /> Free music · Local Gems · Art — <span className="text-[#54E3FF] ml-1">see how to unlock your HOARD →</span>
                  </Button>
                </Link>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/recommend-org">
                  <Button 
                    variant="outline" 
                    className="rounded-full px-8 py-6 text-lg font-medium border-2 border-[#2E5C55] text-[#2E5C55] hover:bg-[#2E5C55] hover:text-white transition-all duration-300"
                    data-testid="hero-refer-btn"
                  >
                    <Heart className="mr-2 h-5 w-5" />
                    Refer an Org
                  </Button>
                </Link>
              </div>
            </div>
            <div className="animate-fade-in-up animation-delay-200">
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_donate-discover/artifacts/xj4mci1p_stars4peace.org%20starflower.png"
                    alt="Stars 4 Peace"
                    className="w-full h-full object-cover"
                  />
                </div>
                <Link
                  to="/our-origins"
                  className="absolute top-4 sm:top-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-[#1E3A8A] shadow-lg hover:bg-[#F2F6FF] transition-colors no-underline whitespace-nowrap"
                  data-testid="hero-star-values-btn"
                >
                  <StarPathMark className="h-6 w-6 ring-[#1E3A8A]/30" /> Our values &amp; who we are
                </Link>
                <Link
                  to="/map"
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-[#E76F51] px-5 py-2.5 text-xs sm:text-sm font-extrabold uppercase tracking-wide text-white shadow-lg hover:bg-[#d65f42] transition-colors no-underline whitespace-nowrap"
                  data-testid="hero-map-btn"
                >
                  Get into the Field! <span className="font-semibold normal-case tracking-normal opacity-90">(org map)</span>
                </Link>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
                  <a href="#causes" onClick={(e) => { e.preventDefault(); document.getElementById("causes")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="flex items-center gap-3 hover:opacity-80 transition-opacity no-underline" data-testid="hero-orgs-card">
                    <div className="w-12 h-12 rounded-full bg-[#E76F51]/10 flex items-center justify-center">
                      <Heart className="h-6 w-6 text-[#E76F51]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">{stats.organizations || 0} Organizations</p>
                      <p className="text-sm text-[#5C5C5C]">Vetted with Stars 4 Peace</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Featured Artist spotlights — two side-by-side hero cards under
                  the starflower: Walker Sherman + Dain Norman, each linking to
                  their artist page. */}
              <div className="mt-12 grid sm:grid-cols-2 gap-4" data-testid="hero-artists-bands-row">
                {featuredArtists.map((artist) => (
                <Link
                  key={artist.slug}
                  to={`/artists/${artist.slug}`}
                  className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group no-underline"
                  data-testid={`hero-featured-artist-${artist.slug}`}
                >
                  <div className="grid grid-cols-[120px_1fr] items-stretch">
                    <div className="overflow-hidden bg-[#1A1A2E]">
                      <img
                        src={artist.photo_url}
                        alt={artist.name}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 flex flex-col justify-center">
                      <span className="text-[10px] uppercase tracking-widest text-[#FFB347] font-bold mb-1">⭐ Meet Artists on THE WALL</span>
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] leading-tight mb-1">{artist.name}</h3>
                      <p className="text-xs text-[#5C5C5C] mb-2">
                        {artist.city || "Tacoma"} · and the full roster
                      </p>
                      <span className="text-xs font-semibold text-[#2E5C55] group-hover:text-[#E76F51] transition-colors inline-flex items-center gap-1">
                        Meet {artist.name.split(" ")[0]} →
                      </span>
                    </div>
                  </div>
                </Link>
                ))}
              </div>

              {/* Soft pointer to the SoundLife hub — added so visitors landing
                  on the homepage have an obvious door into /SoundLife. */}
              <Link
                to="/SoundLife"
                className="mt-4 flex items-center justify-between gap-4 bg-gradient-to-r from-[#3b1d6e] via-[#5a2a8a] to-[#7e4cb8] text-white rounded-2xl px-5 py-4 shadow-lg hover:shadow-xl transition-shadow no-underline"
                data-testid="hero-soundlife-hub-cta"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl" aria-hidden="true">♪</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFD23F] font-bold">SoundLife · The Hub</p>
                    <p className="font-['Playfair_Display'] text-lg leading-tight">Calendars, tours, featured artists &mdash; in one place.</p>
                  </div>
                </div>
                <span className="text-sm font-bold uppercase tracking-wider text-[#FFD23F] whitespace-nowrap">Enter the Hub →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Organizations — Featured + Browse-by-Cause (shared with /how-it-works) */}
      <OrgsSection organizations={allOrganizations} causes={causes} />

      {/* This Music Saves Lives — same visual as the SoundLife Hub "bands"
          section. User asked to replace the old "Explore Your Rewards (the
          Hoard)" 3-card preview here with this hub-style block verbatim.
          Keeping the hub component inlined (instead of importing it) so the
          rest of the home page styling/spacing stays consistent and the hub
          page itself isn't side-affected. */}
      <section
        className="rounded-3xl overflow-hidden shadow-lg border border-[#E2E0DC] mx-4 md:mx-12 my-12 max-w-7xl xl:mx-auto"
        data-testid="landing-this-music-saves-lives"
      >
        <div className="relative bg-gradient-to-br from-[#1f4d3a] via-[#2E5C55] to-[#1a3a2c] p-8 md:p-14">
          <div className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold mb-5" style={{ color: "#F2CC8F" }}>
            ♪  BANDS · AMPLIFY THE DIVIDE
          </div>
          <h2
            className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold leading-[1.0] mb-6 max-w-4xl text-white"
          >
            This Music Saves Lives:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-8 items-center">
            <div>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-5">
                Where THE WALL goes, awareness — and goodwill, grow. Get new fans and promotionals while orgs get much-needed support and audiences all around the Sound get free access to resources.
              </p>
              <Link
                to="/for-bands"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold uppercase tracking-wider text-sm md:text-base mt-2 transition-colors shadow-lg text-white"
                style={{ background: "#E76F51" }}
                data-testid="landing-this-music-saves-lives-cta"
              >
                GET YOUR MUSIC ON THE WALL
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-black/10">
              <img
                src="https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/dlad1gog_dolly%20the%20llama%20this%20music%20saves%20lives.png"
                alt="Dolly the Llama — This Music Saves Lives"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="landing-this-music-saves-lives-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Discovery: Calendar + Guide */}
      <section className="py-24 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wider uppercase text-[#E76F51] mb-4 block">
              What&apos;s Happening · Where to Go
            </span>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-4">
              Discover Your Neighborhood
            </h2>
            <p className="text-xl leading-relaxed font-light text-[#5C5C5C] max-w-2xl mx-auto">
              Two ways to plug into the Sound: see what&apos;s on this week, or browse the businesses powering the community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Community Calendar Card */}
            <Link to="/events" className="group" data-testid="landing-calendar-card">
              <Card className="bg-white border-[#E2E0DC] hover:-translate-y-2 hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                <div className="h-48 bg-gradient-to-br from-[#2E5C55] via-[#3a7a70] to-[#5B8FB9] flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20">
                    {/* Decorative dots representing event calendar */}
                    <div className="grid grid-cols-7 gap-1 p-4 h-full">
                      {Array.from({ length: 35 }).map((_, i) => (
                        <div
                          key={`cal-dot-${i}`}
                          className="aspect-square rounded-full"
                          style={{
                            backgroundColor: i % 5 === 0 ? "#E57C23" : i % 4 === 0 ? "#9B59B6" : i % 3 === 0 ? "#27AE60" : "transparent",
                            opacity: i % 5 === 0 || i % 4 === 0 || i % 3 === 0 ? 0.8 : 0,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="relative z-10 flex flex-col items-center text-white">
                    <CalendarIcon className="h-14 w-14 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="text-5xl font-bold leading-none">{upcomingEventCount}</div>
                    <p className="text-sm text-white/80 mt-1">upcoming this year</p>
                  </div>
                </div>
                <CardContent className="p-7">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#1A1A1A] group-hover:text-[#2E5C55] transition-colors">Community Calendar</h3>
                  </div>
                  <p className="text-[#5C5C5C] text-base mb-4">
                    Live music, karaoke, open mics, art openings, fundraisers, food specials, markets, workshops — color-coded and tappable.
                  </p>
                  {/* Category pills */}
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {[
                      { label: "Live Music", color: "#2E86AB", bg: "#E9F4F9" },
                      { label: "Karaoke", color: "#9B59B6", bg: "#F5EDF8" },
                      { label: "Art Show", color: "#C0392B", bg: "#FBE9E7" },
                      { label: "Fundraiser", color: "#27AE60", bg: "#E9F7EF" },
                      { label: "+4 more", color: "#5C5C5C", bg: "#F5F5F5" },
                    ].map((p) => (
                      <span
                        key={p.label}
                        className="text-[11px] rounded-full px-2.5 py-0.5 font-medium"
                        style={{ backgroundColor: p.bg, color: p.color }}
                      >
                        {p.label}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#E76F51] font-medium group-hover:gap-3 transition-all">
                    Browse the Calendar <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* Hoard Guide Card */}
            <Link to="/trove-guide" className="group" data-testid="landing-guide-card">
              <Card className="bg-white border-[#E2E0DC] hover:-translate-y-2 hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                <div className="h-48 bg-gradient-to-br from-[#E57C23] via-[#E76F51] to-[#C0392B] flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20">
                    {/* Decorative storefronts */}
                    <div className="grid grid-cols-4 gap-3 p-6 h-full items-center">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div
                          key={`storefront-${i}`}
                          className="rounded bg-white/40"
                          style={{ height: `${30 + (i % 4) * 12}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="relative z-10 flex flex-col items-center text-white">
                    <Store className="h-14 w-14 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="text-5xl font-bold leading-none">{businessCount}</div>
                    <p className="text-sm text-white/80 mt-1">local businesses</p>
                  </div>
                </div>
                <CardContent className="p-7">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#1A1A1A] group-hover:text-[#2E5C55] transition-colors"><Hoard /> Guide</h3>
                  </div>
                  <p className="text-[#5C5C5C] text-base mb-4">
                    Restaurants, retail, art galleries, services and more — discover vetted local businesses on a map, see their deals, and support your neighbors.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {[
                      { label: "Restaurants", color: "#E57C23", bg: "#FFF3E6" },
                      { label: "Coffee & Tea", color: "#8B5E3C", bg: "#FBF3ED" },
                      { label: "Retail", color: "#5B8FB9", bg: "#EDF4FA" },
                      { label: "Art & Galleries", color: "#9B59B6", bg: "#F5EDF8" },
                      { label: "+4 more", color: "#5C5C5C", bg: "#F5F5F5" },
                    ].map((p) => (
                      <span
                        key={p.label}
                        className="text-[11px] rounded-full px-2.5 py-0.5 font-medium"
                        style={{ backgroundColor: p.bg, color: p.color }}
                      >
                        {p.label}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#E76F51] font-medium group-hover:gap-3 transition-all">
                    Explore the Guide <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Up Next strip — pulls 3 nearest events from the calendar */}
          {nextEvents.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#1A1A1A] flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-[#E76F51]" /> Up Next
                </h3>
                <Link to="/events" className="text-sm text-[#2E5C55] hover:text-[#E76F51] font-medium inline-flex items-center gap-1 transition-colors">
                  See all events <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="landing-up-next">
                {nextEvents.map((e) => {
                  const fmt = (iso) => {
                    if (!iso) return "";
                    try {
                      const d = new Date(iso);
                      return d.toLocaleDateString([], { month: "short", day: "numeric", weekday: "short" });
                    } catch { return ""; }
                  };
                  return (
                    <Link
                      key={e.event_id}
                      to="/events"
                      className="bg-white rounded-xl border border-[#E2E0DC] p-4 hover:border-[#2E5C55]/30 hover:shadow-sm transition-all flex items-center gap-3"
                    >
                      <div className="flex-shrink-0 bg-[#2E5C55] text-white rounded-lg w-14 h-14 flex flex-col items-center justify-center text-center">
                        <span className="text-[10px] uppercase tracking-wider opacity-80">{fmt(e.start_datetime).split(",")[0] || ""}</span>
                        <span className="text-lg font-bold leading-none">{new Date(e.start_datetime).getDate()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-[#5C5C5C] uppercase tracking-wider mb-1 truncate">{e.category?.replace(/-/g, " ")}</p>
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{e.title}</p>
                        {e.venue_name && <p className="text-xs text-[#5C5C5C] truncate">{e.venue_name}</p>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Card className="bg-[#E8E6E1] border-none overflow-hidden">
            <CardContent className="p-12 md:p-16 relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-4">
                    Add YOUR Work to THE WALL
                  </h2>
                  <p className="text-xl leading-relaxed text-[#5C5C5C] mb-8">
                    Musicians: Add Music; Artists: Add Art; Businesses: Add Discounts & Specials. Everybody WINS!
                  </p>
                  <Link to={user ? "/contribute" : "/auth"}>
                    <Button 
                      className="bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full px-8 py-6 text-lg font-medium"
                      data-testid="become-contributor-btn"
                    >
                      Contribute Now
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
                <div className="hidden md:block">
                  <img 
                    src="https://images.unsplash.com/photo-1653538532968-a64445d7f80c"
                    alt="Music studio"
                    className="rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works — at the very bottom; the tour is the guided version */}
      <section id="how-it-works" className="py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-4">
              How THE WALL Works
            </h2>
            <p className="text-xl leading-relaxed font-light text-[#5C5C5C] max-w-2xl mx-auto">
              Four ways to plug into the South Sound — give where it matters, find what you need.
            </p>
            <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("wall:open-tour"))} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] text-white px-6 py-3 font-bold hover:bg-[#172e6e] transition-colors" data-testid="how-it-works-tour-btn">
              <StarPathMark className="h-6 w-6 ring-white/40" /> Which door is yours? Take the 60-second tour
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "1",
                icon: Heart,
                color: "#E76F51",
                title: "Donate & Unlock the Hoard",
                desc: "Give $20+ to any vetted local 501(c)3, send us your receipt, and unlock exclusive music, art, and deals from neighbors who care.",
                cta: { to: "/", label: "Browse Orgs" }
              },
              {
                step: "2",
                icon: BookOpen,
                color: "#2E5C55",
                title: "Resource Library",
                desc: "A vetted directory of help: shelters, food banks, domestic-violence support, mental-health resources, and more. No sign-in needed.",
                cta: { to: "/resources", label: "Find Help" }
              },
              {
                step: "3",
                icon: CalendarIcon,
                color: "#2E86AB",
                title: "Community Calendar",
                desc: "Live music, karaoke, open mics, art openings, fundraisers — the South Sound's cultural heartbeat in one place.",
                cta: { to: "/events", label: "What's On" }
              },
              {
                step: "4",
                icon: Store,
                color: "#E57C23",
                title: "Hoard Guide",
                desc: "A growing map of local businesses supporting the mission. Find your new favorite spot — and the deal they're offering this week.",
                cta: { to: "/trove-guide", label: "Explore Businesses" }
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.step} className="bg-[#F9F8F6] border-none hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col">
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div
                      className="w-12 h-12 rounded-full text-white flex items-center justify-center mb-4"
                      style={{ backgroundColor: item.color }}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A] mb-2">{item.title}</h3>
                    <p className="text-[#5C5C5C] leading-relaxed text-sm flex-1 mb-4">{item.desc}</p>
                    <Link
                      to={item.cta.to}
                      className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                      style={{ color: item.color }}
                    >
                      {item.cta.label} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* We promote local events & highlight local businesses — Porchfest as the example */}
          <div className="mt-14 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center" data-testid="landing-promote-events">
            <div>
              <span className="text-sm font-medium tracking-wider uppercase text-[#E76F51] mb-3 block">We promote local events &amp; highlight local businesses</span>
              <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#1A1A1A] mb-4">
                Your event, org or band gets a real home on THE WALL
              </h3>
              <p className="text-lg leading-relaxed font-light text-[#5C5C5C] mb-4">
                We build event pages with maps, schedules, flyers and poster walls, put them on the Community Calendar, and send people to the local businesses around them. Tacoma Porchfest is just one successful example — this summer THE WALL carried its porch map, both days&apos; schedules and a community poster wall for thousands of neighbors.
              </p>
              <p className="text-base text-[#5C5C5C] mb-6">
                Next up: a free, build-it-yourself website page for events, organizations and musicians. For now, reach out and we&apos;ll build yours.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/events/suggest" className="inline-flex items-center gap-2 rounded-full bg-[#2E5C55] hover:bg-[#254b45] text-white px-6 py-3 font-semibold transition-colors" data-testid="landing-promote-contact">
                  Promote your event <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/events" className="inline-flex items-center gap-2 rounded-full border border-[#E2E0DC] bg-white hover:border-[#E76F51] text-[#1A1A1A] px-6 py-3 font-semibold transition-colors" data-testid="landing-promote-calendar">
                  See the calendar
                </Link>
              </div>
            </div>
            <Link to="/porchfest" className="block" data-testid="landing-porchfest-example">
              <div className="flex items-center gap-4 bg-[#1A1A1A] rounded-2xl p-4 sm:p-5 border-2 border-[#E76F51] hover:shadow-xl transition-shadow">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/xipljtog_Screen%20Shot%202026-07-15%20at%2011.53.01%20AM.png"
                  alt="Tacoma Porchfest 2026 official poster"
                  className="h-24 sm:h-28 w-auto rounded-lg shadow-md shrink-0"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[#E76F51] text-[10px] font-bold uppercase tracking-[0.25em] mb-1">Example · Tacoma Porchfest 2026</p>
                  <p className="text-white font-['Playfair_Display'] text-lg sm:text-2xl font-semibold leading-snug">
                    Porch map, schedules &amp; the Posted Wall
                  </p>
                  <p className="text-white/70 text-sm mt-1">
                    See THE WALL&apos;s festival guide for July 18 &amp; 19 →
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer showReferralForm={true} />
      <WelcomeTour />
    </div>
  );
};

export default LandingPage;

// Compact org card used by featured row + cause accordion
